import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import {
  SIGNAL_DESCRIPTION_MAX_LENGTH,
  SIGNAL_NAME_MAX_LENGTH,
  type SignalMetadataSchema,
} from "@repo/app-types/entities/signal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { IconChevronDown } from "@tabler/icons-react";
import type { FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { DiffableField } from "../../../components/diffableField.js";
import { ResourceSaveFooter } from "../../../components/resourceSaveFooter.js";
import { MetadataSchemaBuilder } from "./metadataSchemaBuilder.js";
import { SignalPlaybookScopeField } from "./signalPlaybookScopeField.js";
import {
  SignalTestResultPill,
  type SignalTestResultPillState,
} from "./signalTestResultPill.js";

export interface SignalEditorContainerProps {
  name: string;
  description: string;
  /** The saved description, which "Show changes" diffs against. */
  savedDescription: string | undefined;
  metadataSchema: SignalMetadataSchema;
  playbookScope: PlaybookSelection;
  error: string | null;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onMetadataSchemaChange: (value: SignalMetadataSchema) => void;
  onPlaybookScopeChange: (value: PlaybookSelection) => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  isSaveDisabled: boolean;
  hasUnsavedChanges: boolean;
  onTestSignalClick: () => void;
  onConfigureTestClick: () => void;
  isTestSignalDisabled: boolean;
  testResultPillState: SignalTestResultPillState | null;
  onViewMatches: () => void;
}

export const SignalEditorContainer: FC<SignalEditorContainerProps> = ({
  name,
  description,
  savedDescription,
  metadataSchema,
  playbookScope,
  error,
  onNameChange,
  onDescriptionChange,
  onMetadataSchemaChange,
  onPlaybookScopeChange,
  onCancel,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
  isSaveDisabled,
  hasUnsavedChanges,
  onTestSignalClick,
  onConfigureTestClick,
  isTestSignalDisabled,
  testResultPillState,
  onViewMatches,
}) => {
  // Saved trimmed, so whitespace at either end isn't a change.
  const originalDescription =
    savedDescription === description.trim() ? description : savedDescription;

  return (
    <div className="flex h-full flex-col">
      {/* Top toolbar */}
      <div className="relative z-10 flex items-center justify-end bg-card px-4 py-2 after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-6 after:h-6 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']">
        <div className="flex items-center gap-2">
          {onDelete ? (
            <Button
              variant={ButtonVariant.Warning}
              onClick={onDelete}
              isDisabled={isDeleting}
              size={Size.SMALL}
            >
              Delete
            </Button>
          ) : null}
          <div className="flex items-stretch">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onTestSignalClick}
              isDisabled={isTestSignalDisabled}
              size={Size.SMALL}
              className="rounded-r-none"
            >
              Test signal
            </Button>
            <DialogTrigger>
              <Button
                variant={ButtonVariant.Secondary}
                icon={<IconChevronDown className="h-4 w-4" />}
                isDisabled={isTestSignalDisabled}
                size={Size.SMALL}
                className="-ml-px rounded-l-none px-2"
              />
              <SimpleMenuPopover placement="bottom end">
                <SimpleMenu aria-label="Test signal options">
                  <SimpleMenuItem onAction={onConfigureTestClick}>
                    Configure test…
                  </SimpleMenuItem>
                </SimpleMenu>
              </SimpleMenuPopover>
            </DialogTrigger>
          </div>
        </div>
      </div>

      {/* Form content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto flex max-w-xl flex-col space-y-6">
          {testResultPillState ? (
            <SignalTestResultPill
              state={testResultPillState}
              onViewMatches={onViewMatches}
            />
          ) : null}

          <div>
            <div className="mb-1">
              <Label size={Size.SMALL}>Name</Label>
            </div>
            <Input
              id="signal_name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Signal name"
              maxLength={SIGNAL_NAME_MAX_LENGTH}
            />
          </div>

          <div>
            <DiffableField
              label={<Label size={Size.SMALL}>Description</Label>}
              value={description}
              onChange={onDescriptionChange}
              originalValue={originalDescription}
              placeholder="e.g. Customer mentioning one of our competitors"
              maxLength={SIGNAL_DESCRIPTION_MAX_LENGTH}
              isMarkdown
              hasDiffMinimap
              description="A natural language definition of the conversational pattern to detect."
              editorClassName="h-60"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label size={Size.SMALL}>Playbooks</Label>
            <SignalPlaybookScopeField
              value={playbookScope}
              onChange={onPlaybookScopeChange}
            />
            <FieldDescription>
              The playbooks and voice agents whose calls this signal is detected
              in.
            </FieldDescription>
          </div>

          <div>
            <MetadataSchemaBuilder
              value={metadataSchema}
              onChange={onMetadataSchemaChange}
            />
          </div>

          {error ? <FieldError>{error}</FieldError> : null}
        </div>
      </div>

      {hasUnsavedChanges ? (
        <ResourceSaveFooter
          onCancel={onCancel}
          onSave={onSave}
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
        />
      ) : null}
    </div>
  );
};
